import {memo} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button/Button'
import {AlertCircleIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledMessageFooter, StyledMessageForm, StyledFormActions, StyledUrgentButton} from './style.ts'

const NewMessageFormValidationSchema = z.object({
    message: z.string().min(1),
    isUrgent: z.boolean()
})

type NewMessageFormData = z.infer<typeof NewMessageFormValidationSchema>

interface NewMessageFormProps {
    onSubmit: (data: NewMessageFormData) => void
    isLoading?: boolean
    isSending?: boolean
}

const NewMessageFormComponent = ({onSubmit, isLoading = false, isSending = false}: NewMessageFormProps) => {
    const {t} = useTranslation()

    const {
        register,
        handleSubmit,
        formState: {isValid},
        setValue,
        watch,
        reset
    } = useForm<NewMessageFormData>({
        resolver: zodResolver(NewMessageFormValidationSchema),
        defaultValues: {
            message: '',
            isUrgent: false
        }
    })

    const isUrgent = watch('isUrgent')

    const handleFormSubmit = (data: NewMessageFormData) => {
        onSubmit(data)
        reset()
    }

    return (
        <StyledMessageFooter>
            <StyledMessageForm onSubmit={handleSubmit(handleFormSubmit)}>
                <TextArea
                    placeholder={t('conversations:type_message')}
                    rows={3}
                    disabled={isLoading}
                    {...register('message')}
                />
                <StyledFormActions justify="space-between" align="center" fullWidth>
                    <StyledUrgentButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        $isActive={isUrgent}
                        onClick={() => setValue('isUrgent', !isUrgent)}
                    >
                        <AlertCircleIcon size={16} />
                        {t('conversations:urgent')}
                    </StyledUrgentButton>

                    <Flexbox gap={2}>
                        <Button type="submit" variant="primary" disabled={!isValid || isSending || isLoading} size="md">
                            <Send01Icon />
                        </Button>
                    </Flexbox>
                </StyledFormActions>
            </StyledMessageForm>
        </StyledMessageFooter>
    )
}

export const NewMessageForm = memo(NewMessageFormComponent)
